/**
 * Image utility functions for client-side compression and validation
 */

import imageCompression from 'browser-image-compression';

/**
 * Maximum file size before compression (2MB)
 */
export const MAX_IMAGE_SIZE_BYTES = 3 * 1024 * 1024;

/**
 * Target file size after compression (~500KB)
 */
export const TARGET_COMPRESSED_SIZE_BYTES = 500 * 1024;

/**
 * Accepted image MIME types
 */
export const ACCEPTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
];

/**
 * Validates that a file is an acceptable image type and size
 * @param file - The file to validate
 * @returns Error message if invalid, null if valid
 */
export function validateImageFile(file: File): string | null {
  // Check file type
  if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
    return 'Please select a valid image file (JPEG, PNG, WebP, or GIF)';
  }

  // Check file size
  if (file.size > MAX_IMAGE_SIZE_BYTES) {
    return `Image size must be less than ${MAX_IMAGE_SIZE_BYTES / (1024 * 1024)}MB`;
  }

  return null;
}

/**
 * Compresses an image file to approximately the target size
 * @param file - The image file to compress
 * @returns Compressed Blob
 */
export async function compressImage(file: File): Promise<Blob> {
  const options = {
    maxSizeMB: TARGET_COMPRESSED_SIZE_BYTES / (1024 * 1024), // ~0.5MB
    maxWidthOrHeight: 1920, // Max dimension to maintain reasonable quality
    useWebWorker: true, // Use web worker for better performance
    fileType: file.type, // Preserve original file type
  };

  try {
    const compressedFile = await imageCompression(file, options);
    return compressedFile;
  } catch (error) {
    throw new Error(
      `Failed to compress image: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

